import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, User, Phone, MapPin, Check } from 'lucide-react';
import {
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
} from '@roger/ui-components';
import {
  updatePatient,
  client,
  getAvailableClinicians,
  getCurrentEpisode,
  updateCurrentEpisodeCaseManager,
} from '../../services/api';
import type {
  PatientResponse,
  PatientUpdate,
  Gender,
  AvailableClinicianResponse,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import {
  parseISOToLocalDate,
  formatDateToISO,
  formatDateToMMDDYYYY,
} from '../../utils/dateUtils';
import { formatSSN } from '../../utils/intake';
import { GENDER_OPTIONS, US_STATES } from '../../constants/options';

interface PatientInfoTabProps {
  patient: PatientResponse;
  onPatientUpdated: (patient: PatientResponse) => void;
  onCaseManagerUpdated?: (name: string | null) => void;
}

type FormState = {
  first_name: string;
  last_name: string;
  middle_name: string;
  date_of_birth: Date | undefined;
  gender: Gender | '';
  ssn: string;
  mbi: string;
  mrn: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
};

function patientToForm(patient: PatientResponse): FormState {
  return {
    first_name: patient.first_name ?? '',
    last_name: patient.last_name ?? '',
    middle_name: patient.middle_name ?? '',
    date_of_birth: patient.date_of_birth
      ? (parseISOToLocalDate(patient.date_of_birth) ?? undefined)
      : undefined,
    gender: (patient.gender as Gender) ?? '',
    ssn: patient.ssn ? formatSSN(patient.ssn) : '',
    mbi: patient.mbi ?? '',
    mrn: patient.mrn ?? '',
    phone: patient.phone ?? '',
    email: patient.email ?? '',
    address: patient.address ?? '',
    city: patient.city ?? '',
    state: patient.state ?? '',
    zip_code: patient.zip_code ?? '',
  };
}

const AUTOSAVE_DELAY_MS = 800;

export const PatientInfoTab: React.FC<PatientInfoTabProps> = ({
  patient,
  onPatientUpdated,
  onCaseManagerUpdated,
}) => {
  const { error: showError } = useToast();
  const [form, setForm] = useState<FormState>(() => patientToForm(patient));
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');

  const [caseManagers, setCaseManagers] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [currentCaseManagerId, setCurrentCaseManagerId] = useState<
    string | null
  >(null);
  const [episodeStartDate, setEpisodeStartDate] = useState<string | null>(null);
  const [episodeEndDate, setEpisodeEndDate] = useState<string | null>(null);
  const [physicianName, setPhysicianName] = useState<string | null>(null);
  const [episodeLoaded, setEpisodeLoaded] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFormRef = useRef(form);
  latestFormRef.current = form;
  const isMountRef = useRef(true);

  useEffect(() => {
    setForm(patientToForm(patient));
    isMountRef.current = true;
  }, [patient]);

  useEffect(() => {
    const fetchEpisodeAndCaseManagers = async () => {
      try {
        const episodeResponse = await getCurrentEpisode({
          client,
          path: { patient_id: patient.id },
        });

        if (episodeResponse?.data) {
          setCurrentCaseManagerId(episodeResponse.data.case_manager_id ?? null);
          setEpisodeStartDate(episodeResponse.data.start_date ?? null);
          setEpisodeEndDate(episodeResponse.data.end_date ?? null);
          setPhysicianName(episodeResponse.data.physician_name ?? null);

          const clinicId = episodeResponse.data.agency_clinic_id;
          if (clinicId) {
            const cliniciansResponse = await getAvailableClinicians({
              client,
              query: { clinic_id: clinicId },
            });
            if (cliniciansResponse?.data) {
              setCaseManagers(
                cliniciansResponse.data.filter(c => c.is_case_manager)
              );
            }
          }
        }
      } catch {
        // Patient may not have an episode yet
      } finally {
        setEpisodeLoaded(true);
      }
    };
    fetchEpisodeAndCaseManagers();
  }, [patient.id]);

  const isDirty = useCallback(
    (current: FormState) => {
      const original = patientToForm(patient);
      return (Object.keys(original) as (keyof FormState)[]).some(key => {
        const a = current[key];
        const b = original[key];
        if (a instanceof Date && b instanceof Date) {
          return a.getTime() !== b.getTime();
        }
        if (a instanceof Date || b instanceof Date) {
          return (a === undefined) !== (b === undefined);
        }
        return a !== b;
      });
    },
    [patient]
  );

  const doSave = useCallback(
    async (formSnapshot: FormState) => {
      if (!formSnapshot.first_name.trim() || !formSnapshot.last_name.trim()) {
        return;
      }
      if (!isDirty(formSnapshot)) return;

      setSaveStatus('saving');
      try {
        const body: PatientUpdate = {
          first_name: formSnapshot.first_name.trim(),
          last_name: formSnapshot.last_name.trim(),
          middle_name: formSnapshot.middle_name.trim() || null,
          date_of_birth: formSnapshot.date_of_birth
            ? formatDateToISO(formSnapshot.date_of_birth)
            : null,
          gender: formSnapshot.gender || null,
          ssn: formSnapshot.ssn.replace(/\D/g, '') || null,
          mbi: formSnapshot.mbi.trim() || null,
          phone: formSnapshot.phone.trim() || null,
          email: formSnapshot.email.trim() || null,
          address: formSnapshot.address.trim() || null,
          city: formSnapshot.city.trim() || null,
          state: formSnapshot.state || null,
          zip_code: formSnapshot.zip_code.trim() || null,
        };

        const response = await updatePatient({
          path: { patient_id: patient.id },
          body,
          client,
        });

        if (response?.data) {
          onPatientUpdated(response.data);
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } else {
          const detail =
            (response?.error as { detail?: string })?.detail ??
            'Failed to save patient information';
          setSaveStatus('error');
          showError(detail);
          setTimeout(() => setSaveStatus('idle'), 3000);
        }
      } catch {
        setSaveStatus('error');
        showError('Failed to save patient information');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    },
    [patient.id, isDirty, onPatientUpdated, showError]
  );

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      doSave(latestFormRef.current);
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [form, doSave]);

  const updateField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleCaseManagerChange = async (value: string) => {
    const previousId = currentCaseManagerId;
    setCurrentCaseManagerId(value);
    try {
      const response = await updateCurrentEpisodeCaseManager({
        client,
        path: { patient_id: patient.id },
        body: { case_manager_id: value },
      });
      if (response?.data) {
        const cm = caseManagers.find(c => c.id === value);
        const name = cm
          ? cm.clinician_first_name && cm.clinician_last_name
            ? `${cm.clinician_first_name} ${cm.clinician_last_name}`
            : `${cm.user_first_name} ${cm.user_last_name}`
          : null;
        onCaseManagerUpdated?.(name);
      } else {
        const detail =
          (response?.error as { detail?: string })?.detail ??
          'Failed to update case manager';
        setCurrentCaseManagerId(previousId);
        showError(detail);
      }
    } catch {
      setCurrentCaseManagerId(previousId);
      showError('Failed to update case manager');
    }
  };

  return (
    <div className='space-y-6 max-w-4xl'>
      {/* Auto-save status indicator */}
      <div className='flex justify-end h-5'>
        {saveStatus === 'saving' && (
          <span className='text-xs text-muted-foreground flex items-center gap-1'>
            <Loader2 className='h-3 w-3 animate-spin' />
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className='text-xs text-green-600 flex items-center gap-1'>
            <Check className='h-3 w-3' />
            Saved
          </span>
        )}
        {saveStatus === 'error' && (
          <span className='text-xs text-destructive'>Save failed</span>
        )}
      </div>

      {/* Demographic Information */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <User className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Demographic Information</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='first_name'>
                First Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='first_name'
                value={form.first_name}
                onChange={e => updateField('first_name', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='middle_name'>Middle Name</Label>
              <Input
                id='middle_name'
                value={form.middle_name}
                onChange={e => updateField('middle_name', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='last_name'>
                Last Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='last_name'
                value={form.last_name}
                onChange={e => updateField('last_name', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Date of Birth</Label>
              <DatePicker
                date={form.date_of_birth}
                onDateChange={d => updateField('date_of_birth', d)}
                datePickerSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Gender</Label>
              <Select
                value={form.gender}
                onValueChange={v => updateField('gender', v as Gender)}
              >
                <SelectTrigger selectSize='sm'>
                  <SelectValue placeholder='Select gender' />
                </SelectTrigger>
                <SelectContent>
                  {GENDER_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='ssn'>SSN</Label>
              <Input
                id='ssn'
                value={form.ssn}
                onChange={e => updateField('ssn', formatSSN(e.target.value))}
                placeholder='XXX-XX-XXXX'
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='mbi'>MBI</Label>
              <Input
                id='mbi'
                value={form.mbi}
                onChange={e => updateField('mbi', e.target.value)}
                placeholder='1EG4-TE5-MK72'
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='mrn'>MRN</Label>
              <Input
                id='mrn'
                value={form.mrn}
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <Phone className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Contact Information</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='phone'>Phone</Label>
              <Input
                id='phone'
                value={form.phone}
                onChange={e => updateField('phone', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='email'>Email</Label>
              <Input
                id='email'
                type='email'
                value={form.email}
                onChange={e => updateField('email', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='md:col-span-3 space-y-1.5'>
              <Label htmlFor='address'>Street Address</Label>
              <Input
                id='address'
                value={form.address}
                onChange={e => updateField('address', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='city'>City</Label>
              <Input
                id='city'
                value={form.city}
                onChange={e => updateField('city', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>State</Label>
              <Select
                value={form.state}
                onValueChange={v => updateField('state', v)}
              >
                <SelectTrigger selectSize='sm'>
                  <SelectValue placeholder='Select state' />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(st => (
                    <SelectItem key={st.value} value={st.value}>
                      {st.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='zip_code'>ZIP Code</Label>
              <Input
                id='zip_code'
                value={form.zip_code}
                onChange={e => updateField('zip_code', e.target.value)}
                inputSize='sm'
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Episode */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <MapPin className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Current Episode</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <div className='space-y-1.5'>
              <Label>Case Manager</Label>
              {!episodeLoaded ? (
                <Input
                  value='Loading...'
                  disabled
                  inputSize='sm'
                  className='bg-muted'
                />
              ) : (
                <Select
                  value={currentCaseManagerId ?? ''}
                  onValueChange={handleCaseManagerChange}
                >
                  <SelectTrigger selectSize='sm'>
                    <SelectValue placeholder='Select case manager' />
                  </SelectTrigger>
                  <SelectContent>
                    {caseManagers.map(cm => (
                      <SelectItem key={cm.id} value={cm.id}>
                        {cm.clinician_first_name && cm.clinician_last_name
                          ? `${cm.clinician_first_name} ${cm.clinician_last_name}${cm.clinician_credentials ? `, ${cm.clinician_credentials}` : ''}`
                          : `${cm.user_first_name} ${cm.user_last_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className='space-y-1.5'>
              <Label>Physician</Label>
              <Input
                value={episodeLoaded ? physicianName || '—' : 'Loading...'}
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Episode Start Date</Label>
              <Input
                value={
                  episodeLoaded
                    ? formatDateToMMDDYYYY(episodeStartDate) || '—'
                    : 'Loading...'
                }
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Episode End Date</Label>
              <Input
                value={
                  episodeLoaded
                    ? formatDateToMMDDYYYY(episodeEndDate) || '—'
                    : 'Loading...'
                }
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
